/**
 * The register field set as typed data (13-17): every FIELDSET §2 column the dashboard writes or shows, per register.
 *
 * Source: `Harmony-QMS/.planning/phases/12.1-register-obligations-coverage/12.1-FIELDSET.md` ("FIELDSET"), which
 * wins over this file (12.1-HANDOFF). Transcribed from:
 * - §3.2 (lodge form labels), else §2's display label, else the live display name (D-13-17-4);
 * - §2's `Required from stage` cell (as `RequiredRule`s keyed by stage) and `Author role` cell;
 * - §1.4 / §1.5 (the kind: dates, UPN vs position);
 * - the §3.2–§3.5 column lists, VERBATIM, as the per-verb lists below (stages are derived from the verbs).
 * The ambiguities met while transcribing are D-13-17-1 … in `13-DECISIONS.md`.
 *
 * Choices are NOT retyped here: `choicesOf` reads `liveColumns.json`, and a value a rule names goes through
 * `liveChoice`, which throws at load if it is not a live choice. Caps come from the live column (255, or 4,000 for a
 * multi-line column, FIELDSET §3.1). `fieldset.test.ts` checks every column and kind against the live schema and
 * reproduces FIELDSET's counts.
 *
 * Not here, by FIELDSET: the calculated `Reference` ("SP; never read", ISS-008: the record's reference is
 * `RecordRow.ref` = `<PREFIX>-<Id>`, D-13-17-3); frozen legacy and retired columns (`ReportingType`, `ReturnToWork`,
 * `RecipientStream`, `RelevantPolicy`); system and card columns (`ExternalId`, CARD-QS-001's nine); the CI engine's
 * `BaselineSnapshot` and `ActionMode`. Person columns appear only as read-only `person-frozen` (§1.2, §1.5).
 */

import { liveChoice, liveChoices, liveColumn, RECORD_REGISTERS } from './live';
import type {
  FieldAuthor,
  FieldKind,
  RecordRegister,
  RecordVerbKey,
  RequiredRule,
  Stage,
} from '../../types/records';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One column of the field set. */
export interface FieldSpec {
  readonly register: RecordRegister;
  /** SharePoint internal name. */
  readonly name: string;
  /** The form label (FIELDSET §3.2), else §2's display label, else the live display name. */
  readonly label: string;
  readonly kind: FieldKind;
  /** Text kinds only: the live `maxLength` (255), or 4,000 for a multi-line column. */
  readonly cap?: number;
  /** The FIELDSET §3 stages that name this column (derived from `verbs`, plus a verb-less writer's stage). */
  readonly stages: readonly Stage[];
  /** The register verbs that write it (FIELDSET §3.3–§3.5 lists). Empty = the dashboard never writes it. */
  readonly verbs: readonly RecordVerbKey[];
  /** Risk only: a `handling` column the treat verb writes (FIELDSET §3.4 "[treat verb]"). */
  readonly treat?: boolean;
  /** When each stage refuses a blank (FIELDSET §1.1, §2). */
  readonly required: Readonly<Partial<Record<Stage, RequiredRule>>>;
  /** FIELDSET §2's author role(s). Empty for a frozen Person column (no writer). */
  readonly author: readonly FieldAuthor[];
  /** A day column that becomes an instant when the time of day is known (§1.4: `OccurredOn`, `ReportedOn`). */
  readonly timeWhenKnown?: boolean;
  /** Shown on the record page only; no verb writes it. */
  readonly displayOnly?: boolean;
}

interface ColumnDef {
  name: string;
  label?: string;
  kind: FieldKind;
  required?: Partial<Record<Stage, RequiredRule>>;
  author: readonly FieldAuthor[];
  timeWhenKnown?: true;
  displayOnly?: true;
  /** A stage that names the column while no dashboard verb writes it (the CI engine's columns, §3.3.5 / §3.5). */
  stages?: readonly Stage[];
}

type VerbLists = Partial<Record<RecordVerbKey, readonly string[]>>;

// ---------------------------------------------------------------------------
// Rule helpers (values checked against liveColumns.json at load)
// ---------------------------------------------------------------------------

const ALWAYS: RequiredRule = 'always';
const OPTIONAL: RequiredRule = 'optional';
/** How a rule compares a blank (`notIn: [BLANK]` = "when the column is given"). */
export const BLANK = '';

function whenIn(register: RecordRegister, column: string, ...values: string[]): RequiredRule {
  return { when: column, in: liveChoice(register, column, ...values) };
}
function whenNotIn(register: RecordRegister, column: string, ...values: string[]): RequiredRule {
  return { when: column, notIn: liveChoice(register, column, ...values) };
}
function whenGiven(column: string): RequiredRule {
  return { when: column, notIn: [BLANK] };
}

/**
 * The client streams (D-12.1-14-5): the four `BusinessUnit` values on which an incident or complaint is about one
 * service recipient, so `RecipientName` is required. The shared `BusinessUnit` set is identical on every register
 * (asserted in `fieldset.test.ts`), so the values are checked against Incidents.
 */
export const CLIENT_STREAMS: readonly string[] = liveChoice(
  'incidents',
  'BusinessUnit',
  'NDIS',
  'Support at Home Package',
  'Both NDIS & Aged Care',
  'Brokered or Private',
);

const CLIENT_STREAM: RequiredRule = { when: 'BusinessUnit', in: CLIENT_STREAMS };

// ---------------------------------------------------------------------------
// Incidents (FIELDSET §2.1, §3.2.1, §3.3.1, §3.4, §3.5)
// ---------------------------------------------------------------------------

const I = 'incidents';
const I_SIRS = whenIn(I, 'ReportabilityDecision', 'SIRS-P1', 'SIRS-P2');
const I_NDIS = whenIn(I, 'ReportabilityDecision', 'NDIS-Reportable');
const I_WORKER = whenIn(I, 'WorkerInjured', 'Yes');
const I_WORKSAFE = whenIn(I, 'WorkSafeNotifiable', 'Yes');

const INCIDENT_COLUMNS: readonly ColumnDef[] = [
  // §3.2.1 lodge (26). 13.2 (D-13.2-P-3): every lodge column but `ReportedByEmail` gains the CM, who completes it
  // after lodge (the `complete` verb); `InjuredWorkerName` takes `InjuredWorkerUPN`'s place (D-13.2-P-2).
  { name: 'Title', label: 'Incident summary', kind: 'text', required: { lodge: ALWAYS }, author: ['reporter', 'CM'] },
  { name: 'BusinessUnit', label: 'Business unit', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter', 'CM'] },
  { name: 'OccurredOn', label: 'When it happened', kind: 'day-existing', timeWhenKnown: true, required: { lodge: ALWAYS }, author: ['reporter', 'CM'] },
  { name: 'Category', label: 'Category', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter', 'CM'] },
  { name: 'Description', label: 'What happened', kind: 'textarea', required: { lodge: ALWAYS }, author: ['reporter', 'CM'] },
  { name: 'Location', label: 'Where it happened', kind: 'textarea', required: { lodge: ALWAYS }, author: ['reporter', 'CM'] },
  { name: 'Witnesses', label: 'Witness names', kind: 'text', required: { lodge: OPTIONAL }, author: ['reporter', 'CM'] },
  { name: 'ImmediateActions', label: 'What was done straight away', kind: 'textarea', required: { lodge: ALWAYS }, author: ['reporter', 'CM'] },
  { name: 'ReportedOn', label: 'When it was identified or reported', kind: 'day-existing', timeWhenKnown: true, required: { lodge: ALWAYS }, author: ['reporter', 'CM'] },
  { name: 'RecipientName', label: 'Client (service recipient)', kind: 'text', required: { lodge: CLIENT_STREAM }, author: ['reporter', 'CM'] },
  { name: 'RecipientClientId', label: 'Client id', kind: 'text', required: { lodge: CLIENT_STREAM, triage: CLIENT_STREAM }, author: ['reporter', 'CM'] },
  { name: 'ReportedByEmail', label: 'Reported by', kind: 'upn', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'Attachments', label: 'Attachments', kind: 'attachments', required: { lodge: OPTIONAL, investigation: OPTIONAL }, author: ['reporter', 'CM', 'HRWHS'] },
  { name: 'IncidentBasis', label: 'Did it happen, or is it alleged or suspected?', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter', 'CM'] },
  { name: 'HarmKind', label: 'Was anyone harmed?', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter', 'CM'] },
  { name: 'HarmDescription', label: 'The impact on each person affected', kind: 'textarea', required: { lodge: whenIn(I, 'HarmKind', 'Harm', 'Potential harm') }, author: ['reporter', 'CM'] },
  { name: 'OtherPersonsInvolved', label: 'Other people involved (one per line, with their role)', kind: 'textarea', required: { lodge: OPTIONAL }, author: ['reporter', 'CM'] },
  { name: 'PersonsContactDetails', label: 'Contact details of the people involved', kind: 'textarea', required: { lodge: OPTIONAL, triage: OPTIONAL }, author: ['reporter', 'CM'] },
  { name: 'WitnessContactDetails', label: 'Witness contact details', kind: 'textarea', required: { lodge: OPTIONAL, triage: OPTIONAL }, author: ['reporter', 'CM'] },
  { name: 'ReportedOnBehalfOf', label: 'Reporting on behalf of', kind: 'text', required: { lodge: OPTIONAL }, author: ['reporter', 'CM'] },
  { name: 'ReporterPhone', label: 'Your phone', kind: 'text', required: { lodge: OPTIONAL }, author: ['reporter', 'CM'] },
  { name: 'ReportedToRole', label: 'Who you told (their role)', kind: 'position', required: { lodge: OPTIONAL }, author: ['reporter', 'CM'] },
  { name: 'ReportedToAt', label: 'When you told them', kind: 'instant', required: { lodge: whenGiven('ReportedToRole') }, author: ['reporter', 'CM'] },
  { name: 'WorkerInjured', label: 'Was a worker injured?', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter', 'CM'] },
  // D-13.2-P-2: the injured worker by name, entered by the CM (support workers have no Harmony account).
  { name: 'InjuredWorkerName', label: "Injured worker's name", kind: 'text', required: { lodge: I_WORKER, triage: I_WORKER }, author: ['reporter', 'CM', 'HRWHS'] },
  // D-13.2-P-2: FieldLink's "I am the injured worker" is its one writer; the dashboard may only clear it (`complete`).
  { name: 'InjuredWorkerUPN', label: 'Injured worker (their own FieldLink report)', kind: 'upn', required: {}, author: ['reporter'] },
  { name: 'InjuredWorkerOccupation', label: "Injured worker's occupation or job title", kind: 'position', required: { lodge: I_WORKER }, author: ['reporter', 'CM'] },
  // §3.3.1 triage (22; the lodge columns above it re-uses are not repeated)
  { name: 'Status', label: 'Status', kind: 'choice', author: ['system', 'CM', 'Oversight'] },
  { name: 'Severity', label: 'Severity', kind: 'choice', required: { triage: ALWAYS }, author: ['CM'] },
  { name: 'ManagingUnit', label: 'Managing unit', kind: 'choice', required: { triage: ALWAYS }, author: ['CM'] },
  { name: 'PositionsResponsible', label: 'Positions responsible', kind: 'position', required: { triage: ALWAYS }, author: ['CM'] },
  { name: 'ExternalNotified', label: 'Other bodies notified (summary)', kind: 'textarea', required: { triage: OPTIONAL }, author: ['CM'] },
  { name: 'ResolveBy', label: 'Resolve by', kind: 'day-existing', required: { triage: ALWAYS }, author: ['CM'] },
  { name: 'HarmConsequences', label: 'Consequences of the harm, if known', kind: 'textarea', required: { triage: OPTIONAL }, author: ['CM'] },
  { name: 'TriageNote', label: 'Triage note (escalation, investigation need, communication, reporting)', kind: 'textarea', required: { triage: ALWAYS }, author: ['CM', 'Oversight'] },
  { name: 'TriagedAt', label: 'Triaged at', kind: 'instant', required: { triage: ALWAYS }, author: ['system'] },
  { name: 'TriagedByUPN', label: 'Triaged by', kind: 'upn', required: { triage: ALWAYS }, author: ['system'] },
  { name: 'PoliceNotified', label: 'Police notified?', kind: 'choice', required: { triage: ALWAYS }, author: ['CM'] },
  { name: 'CognitionLevel', label: 'Level of cognition of each affected individual', kind: 'textarea', required: { triage: I_SIRS }, author: ['CM', 'SaH decider'] },
  { name: 'PoliceNotifiedAt', label: 'Police notified at', kind: 'instant', required: { triage: whenIn(I, 'PoliceNotified', 'Yes') }, author: ['CM'] },
  { name: 'PoliceReference', label: 'Police reference', kind: 'text', required: { triage: OPTIONAL }, author: ['CM'] },
  { name: 'InvestigationRequired', label: 'Investigation required?', kind: 'choice', required: { triage: ALWAYS }, author: ['CM', 'Oversight'] },
  { name: 'InvestigationReason', label: 'Why (or why not) investigate', kind: 'textarea', required: { triage: ALWAYS }, author: ['CM', 'Oversight'] },
  { name: 'HandlerUPN', label: 'Handler', kind: 'upn', required: { triage: OPTIONAL }, author: ['CM'] },
  { name: 'AllegationAgainstWorker', label: 'Allegation against a worker?', kind: 'choice', required: { triage: ALWAYS }, author: ['CM', 'HRWHS'] },
  { name: 'AllegationFirstHeldOn', label: 'Allegation first held on', kind: 'day-new', required: { triage: whenIn(I, 'AllegationAgainstWorker', 'Yes') }, author: ['CM', 'HRWHS'] },
  // 13.2 (D-13.2-P-5): the CM's DRAFT reportability view, and whether HR & WHS join triage. A draft never sets the
  // decision, a kind, `NotificationDueAt` or a notice: those stay with the deciders (D-13.2-A-1).
  { name: 'ReportabilityDraft', label: 'Your view: is it reportable?', kind: 'choice', required: { triage: CLIENT_STREAM }, author: ['CM'] },
  { name: 'ReportabilityDraftNote', label: 'Why you think so', kind: 'textarea', required: { triage: whenIn(I, 'ReportabilityDraft', 'SIRS-P1', 'SIRS-P2', 'NDIS-Reportable', 'WorkSafe', 'Other', 'Unsure') }, author: ['CM'] },
  { name: 'HrWhsTriageNeeded', label: 'Should HR & WHS join triage?', kind: 'choice', required: { triage: ALWAYS }, author: ['CM'] },
  // §3.3.1 reportability (8)
  { name: 'ReportabilityDecision', label: 'Reportability decision', kind: 'choice', required: { triage: ALWAYS }, author: ['SaH decider', 'Oversight'] },
  { name: 'NdisRiKind', label: 'NDIS reportable incident kind', kind: 'choice', required: { triage: I_NDIS }, author: ['Oversight'] },
  { name: 'NdisNoticeClass', label: 'NDIS notification class', kind: 'choice', required: { triage: I_NDIS }, author: ['Oversight'] },
  { name: 'SirsKind', label: 'SIRS incident kind', kind: 'choice', required: { triage: I_SIRS }, author: ['SaH decider'] },
  { name: 'SirsPriorityBasis', label: 'P1 ground and impairment rule relied on', kind: 'textarea', required: { triage: I_SIRS }, author: ['SaH decider'] },
  { name: 'SirsNeglectExclusionRef', label: 'Neglect exclusion relied on (recorded-choice reference)', kind: 'text', required: { triage: OPTIONAL }, author: ['SaH decider'] },
  // 13.2 (D-13.2-P-5): the CM records it at triage too; the decider may amend it.
  { name: 'AwareAt', label: 'When Harmony became aware', kind: 'instant', required: { triage: ALWAYS }, author: ['Oversight', 'SaH decider', 'HRWHS', 'CM'] },
  { name: 'NotificationDueAt', label: 'Notification due', kind: 'instant', required: { triage: ALWAYS }, author: ['system'] },
  // §3.3.1 WHS (22; AwareAt, NotificationDueAt, InjuredWorkerName and the two allegation columns are above)
  { name: 'WorkSafeNotifiable', label: 'WorkSafe notifiable incident?', kind: 'choice', required: { triage: ALWAYS }, author: ['HRWHS'] },
  { name: 'WorkSafeIncidentKind', label: 's 37 kind', kind: 'choice', required: { triage: I_WORKSAFE }, author: ['HRWHS'] },
  { name: 'WorkplaceType', label: 'Workplace type', kind: 'choice', required: { triage: ALWAYS }, author: ['HRWHS'] },
  { name: 'UnderHarmonyControl', label: "Under Harmony's management and control?", kind: 'choice', required: { triage: ALWAYS }, author: ['HRWHS'] },
  { name: 'InjuryNoticeReceivedOn', label: 'Notice of injury received on', kind: 'day-new', required: { triage: OPTIONAL }, author: ['HRWHS'] },
  { name: 'InjuryNoticeMethod', label: 'How the notice was given', kind: 'choice', required: { triage: whenGiven('InjuryNoticeReceivedOn') }, author: ['HRWHS'] },
  { name: 'InjuryNoticeAcknowledgedOn', label: 'Notice acknowledged in writing on', kind: 'day-new', required: { triage: whenGiven('InjuryNoticeReceivedOn') }, author: ['HRWHS'] },
  { name: 'ClaimReceivedOn', label: 'Claim received on', kind: 'day-new', required: { triage: OPTIONAL }, author: ['HRWHS'] },
  { name: 'ClaimType', label: 'Claim type (s 73(1))', kind: 'choice', required: { triage: whenGiven('ClaimReceivedOn') }, author: ['HRWHS'] },
  { name: 'ClaimAcknowledgedOn', label: 'Claim acknowledged in writing on', kind: 'day-new', required: { triage: whenGiven('ClaimReceivedOn') }, author: ['HRWHS'] },
  { name: 'ClaimForwardedOn', label: 'Claim forwarded to WorkSafe on', kind: 'day-new', required: { triage: whenGiven('ClaimReceivedOn') }, author: ['HRWHS'] },
  { name: 'MedicalCertificateReceivedOn', label: 'Medical certificate received on', kind: 'day-new', required: { triage: OPTIONAL }, author: ['HRWHS'] },
  { name: 'MedicalCertificateForwardedOn', label: 'Medical certificate forwarded on', kind: 'day-new', required: { triage: whenGiven('MedicalCertificateReceivedOn') }, author: ['HRWHS'] },
  { name: 'MentalInjuryClaim', label: 'Mental injury claim?', kind: 'choice', required: { triage: whenGiven('ClaimReceivedOn') }, author: ['HRWHS'] },
  { name: 'MentalInjuryNotifiedOn', label: 'Mental injury claim notified to WorkSafe on', kind: 'day-new', required: { triage: whenIn(I, 'MentalInjuryClaim', 'Yes') }, author: ['HRWHS'] },
  { name: 'RtwKnowledgeOn', label: 'Date Harmony knew of the incapacity (RTW clock)', kind: 'day-new', required: { triage: OPTIONAL }, author: ['HRWHS'] },
  { name: 'RtwPlanningStartedOn', label: 'RTW planning started on', kind: 'day-new', required: { triage: OPTIONAL }, author: ['HRWHS'] },
  // §3.3.1 notification (19); "notification (verb)" is transcribed as `always` (D-13-17-6)
  { name: 'ExternalOutcome', label: 'Regulator outcome', kind: 'textarea', required: { notification: ALWAYS }, author: ['Oversight', 'SaH decider', 'HRWHS'] },
  { name: 'NdisInitialNoticeAt', label: 'NDIS initial notice given at', kind: 'instant', required: { notification: ALWAYS }, author: ['Oversight'] },
  { name: 'NdisInitialNoticeMethod', label: 'NDIS initial notice method', kind: 'text', required: { notification: ALWAYS }, author: ['Oversight'] },
  { name: 'NotifierUPN', label: 'Notifier', kind: 'upn', required: { notification: ALWAYS }, author: ['Oversight', 'SaH decider'] },
  { name: 'NotifierPosition', label: "Notifier's position", kind: 'position', required: { notification: ALWAYS }, author: ['Oversight', 'SaH decider'] },
  { name: 'NdisFurtherNoticeAt', label: 'NDIS 5-business-day notice given at', kind: 'instant', required: { notification: ALWAYS }, author: ['Oversight'] },
  { name: 'FurtherActionsProposed', label: 'Further actions proposed', kind: 'textarea', required: { notification: ALWAYS }, author: ['CM', 'Oversight', 'SaH decider'] },
  { name: 'InfoWithheld', label: 'Information withheld (IMRI s 22) and why', kind: 'textarea', required: { notification: OPTIONAL }, author: ['Oversight'] },
  { name: 'RegulatorReference', label: 'Regulator reference', kind: 'text', required: { notification: ALWAYS }, author: ['Oversight', 'SaH decider'] },
  { name: 'FinalReportRequired', label: 'Final report required?', kind: 'choice', required: { notification: ALWAYS }, author: ['Oversight', 'SaH decider'] },
  { name: 'FinalReportDueOn', label: 'Final report due', kind: 'day-new', required: { notification: whenIn(I, 'FinalReportRequired', 'Yes') }, author: ['Oversight', 'SaH decider'] },
  { name: 'FinalReportGivenAt', label: 'Final report given at', kind: 'instant', required: { notification: ALWAYS }, author: ['Oversight', 'SaH decider'] },
  { name: 'SirsNoticeMethod', label: 'SIRS notice method', kind: 'text', required: { notification: ALWAYS }, author: ['SaH decider'] },
  { name: 'SirsNoticeAt', label: 'SIRS notice given at (P1 or P2)', kind: 'instant', required: { notification: ALWAYS }, author: ['SaH decider'] },
  { name: 'SirsAdditionalNoticeAt', label: 'SIRS additional notice given at', kind: 'instant', required: { notification: OPTIONAL }, author: ['SaH decider'] },
  { name: 'WorkSafeNotifiedAt', label: 'WorkSafe notified at', kind: 'instant', required: { notification: I_WORKSAFE }, author: ['HRWHS'] },
  { name: 'WorkSafeNotifiedByUPN', label: 'WorkSafe notified by', kind: 'upn', required: { notification: I_WORKSAFE }, author: ['HRWHS'] },
  { name: 'WorkSafeReference', label: 'WorkSafe reference', kind: 'text', required: { notification: I_WORKSAFE }, author: ['HRWHS'] },
  { name: 'WorkSafeWrittenRecordAt', label: 'Written record given to WorkSafe at', kind: 'instant', required: { notification: I_WORKSAFE }, author: ['HRWHS'] },
  // §3.4 investigation (4; Attachments is above)
  { name: 'OpenDisclosureUsed', label: 'Open disclosure used?', kind: 'choice', required: { closure: ALWAYS }, author: ['CM'] },
  { name: 'OpenDisclosureAt', label: 'Open disclosure at', kind: 'instant', required: { investigation: whenIn(I, 'OpenDisclosureUsed', 'Yes') }, author: ['CM'] },
  { name: 'InvestigationFindings', label: 'Investigation findings', kind: 'textarea', required: { closure: whenIn(I, 'InvestigationRequired', 'Yes') }, author: ['CM'] },
  // §3.5 closure (13; Status and Category are above)
  { name: 'ClosedOn', label: 'Closed on', kind: 'day-existing', required: { closure: ALWAYS }, author: ['Oversight'] },
  { name: 'ClosingComments', label: 'Outcome summary', kind: 'textarea', required: { closure: ALWAYS }, author: ['CM', 'Oversight'] },
  { name: 'SatisfactoryResolution', label: 'How well it was managed and resolved', kind: 'choice', required: { closure: ALWAYS }, author: ['CM'] },
  { name: 'ClosureStatus', label: 'Closure status', kind: 'choice', author: ['system', 'Oversight'] },
  { name: 'Preventable', label: 'Could it have been prevented?', kind: 'choice', required: { closure: ALWAYS }, author: ['CM'] },
  { name: 'ManagementAssessment', label: 'How well it was managed and resolved (assessment)', kind: 'textarea', required: { closure: ALWAYS }, author: ['CM'] },
  { name: 'CorrectiveActionRequired', label: 'Corrective action required?', kind: 'choice', required: { closure: ALWAYS }, author: ['CM'] },
  { name: 'CorrectiveActionNature', label: 'Nature of the corrective action', kind: 'textarea', required: { closure: whenIn(I, 'CorrectiveActionRequired', 'Yes') }, author: ['CM'] },
  { name: 'AffectedViewsConsidered', label: 'Views of the affected persons considered', kind: 'textarea', required: { closure: ALWAYS }, author: ['CM'] },
  { name: 'ResolutionApprovedOn', label: 'Resolution approved on', kind: 'day-new', required: { closure: ALWAYS }, author: ['Oversight'] },
  { name: 'ResolutionApprovedByUPN', label: 'Resolution approved by', kind: 'upn', required: { closure: ALWAYS }, author: ['Oversight'] },
  // 13.2 (D-13.2-P-10): the kept record's `INC-<Id>`, set by `closeAsDuplicate`.
  { name: 'DuplicateOfRef', label: 'Duplicate of', kind: 'text', author: ['Oversight'] },
  // display only
  { name: 'Created', kind: 'instant', author: ['system'], displayOnly: true },
  { name: 'ReportedBy', kind: 'person-frozen', author: [], displayOnly: true },
  { name: 'ResolutionApprovedBy', kind: 'person-frozen', author: [], displayOnly: true },
];

/** FIELDSET §3.2.1 as amended 2026-10-25 (13.2-01): `InjuredWorkerName` takes row 25's place (D-13.2-P-2). */
const INCIDENT_LODGE: readonly string[] = [
  'Title', 'BusinessUnit', 'OccurredOn', 'Category', 'Description', 'Location', 'Witnesses', 'ImmediateActions',
  'ReportedOn', 'RecipientName', 'RecipientClientId', 'ReportedByEmail', 'Attachments', 'IncidentBasis', 'HarmKind',
  'HarmDescription', 'OtherPersonsInvolved', 'PersonsContactDetails', 'WitnessContactDetails', 'ReportedOnBehalfOf',
  'ReporterPhone', 'ReportedToRole', 'ReportedToAt', 'WorkerInjured', 'InjuredWorkerName', 'InjuredWorkerOccupation',
];

const INCIDENT_VERBS: VerbLists = {
  lodge: INCIDENT_LODGE,
  // D-13.2-P-3: every lodge column but `ReportedByEmail` (provenance) and `Attachments` (none uploaded, D-13-L-6),
  // plus `InjuredWorkerUPN` beside the name, which `complete` may only clear (13.2-03's rule).
  complete: INCIDENT_LODGE.filter((c) => c !== 'ReportedByEmail' && c !== 'Attachments').flatMap((c) =>
    c === 'InjuredWorkerName' ? [c, 'InjuredWorkerUPN'] : [c]),
  // §3.3.1 as amended 2026-10-25 (D-13.2-P-5, D-13.2-P-2): the 22, plus AwareAt, the name, the draft and the WHS flag.
  triage: [
    'Status', 'Severity', 'RecipientClientId', 'ManagingUnit', 'PositionsResponsible', 'ExternalNotified', 'ResolveBy',
    'HarmConsequences', 'PersonsContactDetails', 'WitnessContactDetails', 'TriageNote', 'TriagedAt', 'TriagedByUPN',
    'PoliceNotified', 'CognitionLevel', 'PoliceNotifiedAt', 'PoliceReference', 'InvestigationRequired',
    'InvestigationReason', 'HandlerUPN', 'AllegationAgainstWorker', 'AllegationFirstHeldOn',
    'AwareAt', 'InjuredWorkerName', 'ReportabilityDraft', 'ReportabilityDraftNote', 'HrWhsTriageNeeded',
  ],
  reportability: [
    'ReportabilityDecision', 'NdisRiKind', 'NdisNoticeClass', 'SirsKind', 'SirsPriorityBasis', 'SirsNeglectExclusionRef',
    'AwareAt', 'NotificationDueAt',
  ],
  whs: [
    'AwareAt', 'NotificationDueAt', 'WorkSafeNotifiable', 'WorkSafeIncidentKind', 'WorkplaceType', 'UnderHarmonyControl',
    'InjuredWorkerName', 'InjuryNoticeReceivedOn', 'InjuryNoticeMethod', 'InjuryNoticeAcknowledgedOn', 'ClaimReceivedOn',
    'ClaimType', 'ClaimAcknowledgedOn', 'ClaimForwardedOn', 'MedicalCertificateReceivedOn',
    'MedicalCertificateForwardedOn', 'MentalInjuryClaim', 'MentalInjuryNotifiedOn', 'RtwKnowledgeOn',
    'RtwPlanningStartedOn', 'AllegationAgainstWorker', 'AllegationFirstHeldOn',
  ],
  notification: [
    'ExternalOutcome', 'NdisInitialNoticeAt', 'NdisInitialNoticeMethod', 'NotifierUPN', 'NotifierPosition',
    'NdisFurtherNoticeAt', 'FurtherActionsProposed', 'InfoWithheld', 'RegulatorReference', 'FinalReportRequired',
    'FinalReportDueOn', 'FinalReportGivenAt', 'SirsNoticeMethod', 'SirsNoticeAt', 'SirsAdditionalNoticeAt',
    'WorkSafeNotifiedAt', 'WorkSafeNotifiedByUPN', 'WorkSafeReference', 'WorkSafeWrittenRecordAt',
  ],
  handling: ['Attachments', 'OpenDisclosureUsed', 'OpenDisclosureAt', 'InvestigationFindings'],
  // D-13-L-3: a CareManager prepares, Oversight approves; §1.8 for cancel and reopen.
  readyForApproval: [
    'Status', 'Category', 'ClosingComments', 'SatisfactoryResolution', 'Preventable', 'ManagementAssessment',
    'CorrectiveActionRequired', 'CorrectiveActionNature', 'AffectedViewsConsidered',
  ],
  approveAndClose: ['Status', 'ClosedOn', 'ClosureStatus', 'ResolutionApprovedOn', 'ResolutionApprovedByUPN'],
  // ISS-259 (D-13-33): Oversight returns a readied incident, Evaluating → Actioning. A §1.8 move, no column change.
  returnForRework: ['Status'],
  cancel: ['Status', 'ClosureStatus'],
  // §1.8 as amended 2026-10-25 (D-13.2-P-10): Oversight cancels a duplicate, naming the kept record. No ClosedOn.
  closeAsDuplicate: ['Status', 'ClosureStatus', 'DuplicateOfRef'],
  reopen: ['Status', 'ClosedOn', 'ClosureStatus'],
};

// ---------------------------------------------------------------------------
// Complaints (FIELDSET §2.2, §3.2.2, §3.3.2, §3.4, §3.5)
// ---------------------------------------------------------------------------

const C = 'complaints';

const COMPLAINT_COLUMNS: readonly ColumnDef[] = [
  // §3.2.2 lodge (16)
  { name: 'Title', label: 'Complaint summary', kind: 'text', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'Category', label: 'Category', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'BusinessUnit', label: 'Business unit', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'Description', label: 'What the complaint is about', kind: 'textarea', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'ComplainantName', label: 'Complainant name', kind: 'text', required: { lodge: whenNotIn(C, 'Anonymous', 'Yes') }, author: ['reporter'] },
  { name: 'ComplainantPhone', label: 'Complainant phone', kind: 'text', required: { lodge: OPTIONAL }, author: ['reporter'] },
  { name: 'ComplainantEmail', label: 'Complainant email', kind: 'text', required: { lodge: OPTIONAL }, author: ['reporter'] },
  { name: 'ReceivedOn', label: 'Date received', kind: 'day-existing', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'Attachments', label: 'Attachments', kind: 'attachments', required: { lodge: OPTIONAL, investigation: OPTIONAL }, author: ['reporter', 'CM'] },
  { name: 'Anonymous', label: 'Anonymous?', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'OnBehalfOf', label: 'Made on behalf of someone?', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'RecipientName', label: 'Client affected (service recipient)', kind: 'text', required: { lodge: CLIENT_STREAM }, author: ['reporter'] },
  // ISS-258 (FIELDSET §2.2 / §3.3.2 amended 13 Oct 2026, D-13-33): complaint triage may add the id, never required there.
  { name: 'RecipientClientId', label: 'Client id', kind: 'text', required: { lodge: whenGiven('RecipientName'), triage: OPTIONAL }, author: ['reporter', 'CM'] },
  { name: 'Channel', label: 'Made orally or in writing?', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'SourceType', label: 'Who raised it', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'WhistleblowerElection', label: 'Discloser elected to manage this as a complaint?', kind: 'choice', required: { lodge: OPTIONAL }, author: ['reporter'] },
  // §3.3.2 triage (11)
  { name: 'Status', label: 'Status', kind: 'choice', author: ['system', 'CM'] },
  { name: 'ManagingUnit', label: 'Managing unit', kind: 'choice', required: { triage: ALWAYS }, author: ['CM'] },
  { name: 'PositionsResponsible', label: 'Positions responsible', kind: 'position', required: { triage: ALWAYS }, author: ['CM'] },
  { name: 'RelatedTo', label: 'Related items (text echo of Closure Links)', kind: 'textarea', required: { triage: OPTIONAL }, author: ['Oversight', 'CM'] },
  { name: 'AcknowledgedAt', label: 'Acknowledged at', kind: 'instant', required: { triage: ALWAYS }, author: ['CM'] },
  { name: 'ExpectedTimeframe', label: 'Expected timeframe given', kind: 'text', required: { triage: ALWAYS }, author: ['CM'] },
  { name: 'Priority', label: 'Priority', kind: 'choice', required: { triage: ALWAYS }, author: ['CM', 'Oversight'] },
  { name: 'AssessmentNote', label: 'Assessment (nature, seriousness, route)', kind: 'textarea', required: { triage: ALWAYS }, author: ['CM', 'Oversight'] },
  { name: 'InvestigationRequired', label: 'Full investigation required?', kind: 'choice', required: { triage: ALWAYS }, author: ['CM', 'Oversight'] },
  { name: 'RegulatorReferred', label: 'Referred by the Commission?', kind: 'choice', required: { triage: ALWAYS }, author: ['CM'] },
  { name: 'RegulatorResponseDueOn', label: 'Commission response due', kind: 'day-new', required: { triage: whenIn(C, 'RegulatorReferred', 'Yes') }, author: ['CM'] },
  // §3.4 investigation (6; Attachments is above)
  { name: 'WithdrawnOn', label: 'Withdrawn on', kind: 'day-new', required: { investigation: OPTIONAL }, author: ['CM'] },
  { name: 'TargetResolutionOn', label: 'Target resolution date', kind: 'day-new', required: { investigation: ALWAYS }, author: ['CM'] },
  { name: 'CommissionerRouteAdvisedOn', label: 'Commissioner route advised on', kind: 'day-new', required: { closure: ALWAYS }, author: ['CM'] },
  { name: 'AdvocateInvolved', label: 'Advocate or representative involved?', kind: 'choice', required: { closure: ALWAYS }, author: ['CM'] },
  { name: 'OpenDisclosureUsed', label: 'Open disclosure used?', kind: 'choice', required: { closure: ALWAYS }, author: ['CM'] },
  // §3.5 closure (12; Category and Status are above)
  { name: 'ClosureStatus', label: 'Closure status', kind: 'choice', author: ['system', 'CM'] },
  { name: 'Outcome', label: 'Outcome and decisions (with reasons)', kind: 'textarea', required: { closure: ALWAYS }, author: ['CM'] },
  { name: 'ClosedOn', label: 'Closed on', kind: 'day-new', required: { closure: ALWAYS }, author: ['CM'] },
  { name: 'OutcomeNotifiedAt', label: 'Outcome notified at', kind: 'instant', required: { closure: ALWAYS }, author: ['CM'] },
  { name: 'ReviewOptionsGiven', label: 'Review options given?', kind: 'choice', required: { closure: ALWAYS }, author: ['CM'] },
  { name: 'EffectivenessEvaluation', label: 'Effectiveness of the response', kind: 'textarea', required: { closure: ALWAYS }, author: ['CM'] },
  { name: 'ComplainantSatisfied', label: 'Complainant satisfied?', kind: 'choice', required: { closure: ALWAYS }, author: ['CM'] },
  { name: 'TrainingDelivered', label: 'Training delivered in response', kind: 'textarea', required: { closure: OPTIONAL }, author: ['CM', 'HRWHS'] },
  { name: 'UnderlyingReason', label: 'Underlying reason', kind: 'textarea', required: { closure: ALWAYS }, author: ['CM'] },
  { name: 'SystemicIssue', label: 'Systemic issue?', kind: 'choice', required: { closure: ALWAYS }, author: ['CM'] },
  // display only
  { name: 'Created', kind: 'instant', author: ['system'], displayOnly: true },
];

const COMPLAINT_VERBS: VerbLists = {
  lodge: [
    'Title', 'Category', 'BusinessUnit', 'Description', 'ComplainantName', 'ComplainantPhone', 'ComplainantEmail',
    'ReceivedOn', 'Attachments', 'Anonymous', 'OnBehalfOf', 'RecipientName', 'RecipientClientId', 'Channel',
    'SourceType', 'WhistleblowerElection',
  ],
  // §3.3.2 as amended 13 Oct 2026 (ISS-258): `RecipientClientId` added, 11 → 12.
  triage: [
    'Status', 'RecipientClientId', 'ManagingUnit', 'PositionsResponsible', 'RelatedTo', 'AcknowledgedAt',
    'ExpectedTimeframe', 'Priority', 'AssessmentNote', 'InvestigationRequired', 'RegulatorReferred',
    'RegulatorResponseDueOn',
  ],
  handling: [
    'Attachments', 'WithdrawnOn', 'TargetResolutionOn', 'CommissionerRouteAdvisedOn', 'AdvocateInvolved',
    'OpenDisclosureUsed',
  ],
  close: [
    'Category', 'Status', 'ClosureStatus', 'Outcome', 'ClosedOn', 'OutcomeNotifiedAt', 'ReviewOptionsGiven',
    'EffectivenessEvaluation', 'ComplainantSatisfied', 'TrainingDelivered', 'UnderlyingReason', 'SystemicIssue',
  ],
  cancel: ['Status', 'ClosureStatus'],
  reopen: ['Status', 'ClosedOn', 'ClosureStatus'],
};

// ---------------------------------------------------------------------------
// Feedback (FIELDSET §2.3, §3.2.3, §3.3.3, §3.4, §3.5)
// ---------------------------------------------------------------------------

const F = 'feedback';
const F_RAISES = whenIn(F, 'RaisesIssue', 'Yes');

const FEEDBACK_COLUMNS: readonly ColumnDef[] = [
  // §3.2.3 lodge (13)
  { name: 'Title', label: 'Feedback summary', kind: 'text', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'Category', label: 'Type', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'BusinessUnit', label: 'Business unit', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'Description', label: 'The feedback', kind: 'textarea', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'ProviderName', label: 'Name of the person giving it', kind: 'text', required: { lodge: OPTIONAL }, author: ['reporter'] },
  { name: 'ProviderPhone', label: 'Their phone', kind: 'text', required: { lodge: OPTIONAL }, author: ['reporter'] },
  { name: 'ProviderEmail', label: 'Their email', kind: 'text', required: { lodge: OPTIONAL }, author: ['reporter'] },
  { name: 'ReceivedOn', label: 'Date received', kind: 'day-existing', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'Attachments', label: "Attachments (incl. a survey round's analysis)", kind: 'attachments', required: { lodge: OPTIONAL, investigation: OPTIONAL }, author: ['reporter', 'Oversight'] },
  { name: 'Anonymous', label: 'Anonymous?', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'Channel', label: 'Given orally or in writing?', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'SourceType', label: 'Who gave it', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'WhistleblowerElection', label: 'Discloser elected to manage this as feedback?', kind: 'choice', required: { lodge: OPTIONAL }, author: ['reporter'] },
  // §3.3.3 triage (8)
  { name: 'Status', label: 'Status', kind: 'choice', author: ['system', 'CM'] },
  { name: 'ManagingUnit', label: 'Managing unit', kind: 'choice', required: { triage: ALWAYS }, author: ['CM'] },
  { name: 'PositionsResponsible', label: 'Positions responsible', kind: 'position', required: { triage: ALWAYS }, author: ['CM'] },
  { name: 'RelatedTo', label: 'Related items (text echo of Closure Links)', kind: 'textarea', required: { triage: OPTIONAL }, author: ['CM'] },
  { name: 'ClassifiedAsComplaint', label: 'Is this really a complaint?', kind: 'choice', required: { triage: ALWAYS }, author: ['Oversight'] },
  { name: 'AcknowledgedAt', label: 'Acknowledged at', kind: 'instant', required: { triage: ALWAYS }, author: ['CM'] },
  { name: 'ResponseGivenAt', label: 'Response given at', kind: 'instant', required: { closure: ALWAYS }, author: ['CM'] },
  { name: 'RaisesIssue', label: 'Does it raise an issue?', kind: 'choice', required: { triage: ALWAYS }, author: ['CM'] },
  // §3.4 investigation (3; Attachments is above)
  { name: 'WithdrawnOn', label: 'Withdrawn on', kind: 'day-new', required: { investigation: OPTIONAL }, author: ['CM'] },
  { name: 'CommissionerRouteAdvisedOn', label: 'Complaints Commissioner route given on', kind: 'day-new', required: { closure: F_RAISES }, author: ['CM'] },
  // §3.5 closure (5; Status is above)
  { name: 'ClosureStatus', label: 'Closure status', kind: 'choice', author: ['system', 'CM'] },
  { name: 'ClosedOn', label: 'Closed on', kind: 'day-new', required: { closure: ALWAYS }, author: ['CM'] },
  { name: 'EffectivenessEvaluation', label: 'Effectiveness of the response', kind: 'textarea', required: { closure: F_RAISES }, author: ['CM'] },
  { name: 'TrainingDelivered', label: 'Training delivered in response', kind: 'textarea', required: { closure: OPTIONAL }, author: ['CM'] },
  // display only
  { name: 'Created', kind: 'instant', author: ['system'], displayOnly: true },
];

const FEEDBACK_VERBS: VerbLists = {
  lodge: [
    'Title', 'Category', 'BusinessUnit', 'Description', 'ProviderName', 'ProviderPhone', 'ProviderEmail', 'ReceivedOn',
    'Attachments', 'Anonymous', 'Channel', 'SourceType', 'WhistleblowerElection',
  ],
  triage: [
    'Status', 'ManagingUnit', 'PositionsResponsible', 'RelatedTo', 'ClassifiedAsComplaint', 'AcknowledgedAt',
    'ResponseGivenAt', 'RaisesIssue',
  ],
  handling: ['Attachments', 'WithdrawnOn', 'CommissionerRouteAdvisedOn'],
  close: ['Status', 'ClosureStatus', 'ClosedOn', 'EffectivenessEvaluation', 'TrainingDelivered'],
  cancel: ['Status', 'ClosureStatus'],
  reopen: ['Status', 'ClosedOn', 'ClosureStatus'],
};

// ---------------------------------------------------------------------------
// Risk Register (FIELDSET §2.4, §3.2.4, §3.3.4, §3.4, §3.5)
// ---------------------------------------------------------------------------

const R = 'risk';
const R_WHS = whenIn(R, 'RiskCategory', 'WHS');
const R_ACCEPTED = whenIn(R, 'RiskAccepted', 'Yes');

const RISK_COLUMNS: readonly ColumnDef[] = [
  // §3.2.4 lodge (4)
  { name: 'Title', label: 'Risk', kind: 'text', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'RiskCategory', label: 'Risk category', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'BusinessUnit', label: 'Business unit', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter'] },
  { name: 'Description', label: 'What, where, when, why, how; the consequence', kind: 'textarea', required: { lodge: ALWAYS }, author: ['reporter'] },
  // §3.3.4 triage (11) + WHS (1)
  { name: 'Likelihood', label: 'Likelihood', kind: 'choice', required: { triage: ALWAYS }, author: ['Oversight'] },
  { name: 'Consequence', label: 'Consequence', kind: 'choice', required: { triage: ALWAYS }, author: ['Oversight'] },
  { name: 'InherentRiskRating', label: 'Rating before treatment', kind: 'choice', required: { triage: ALWAYS }, author: ['Oversight'] },
  { name: 'ExistingControls', label: 'Existing controls', kind: 'textarea', required: { triage: ALWAYS }, author: ['Oversight'] },
  { name: 'ReviewDate', label: 'Next review due', kind: 'day-existing', required: { triage: ALWAYS, closure: ALWAYS }, author: ['Oversight'] },
  { name: 'Status', label: 'Status', kind: 'choice', author: ['system', 'Oversight'] },
  { name: 'OriginatingItems', label: 'Source items (text echo of Closure Links)', kind: 'textarea', required: { triage: OPTIONAL }, author: ['Oversight'] },
  { name: 'RiskRegisterType', label: 'Corporate or client risk?', kind: 'choice', required: { triage: ALWAYS }, author: ['Oversight'] },
  { name: 'RiskOwnerUPN', label: 'Risk owner', kind: 'upn', required: { triage: ALWAYS }, author: ['Oversight'] },
  { name: 'TreatmentUrgency', label: 'Treatment urgency', kind: 'choice', required: { triage: ALWAYS }, author: ['Oversight'] },
  { name: 'HazardType', label: 'Hazard type', kind: 'choice', required: { triage: R_WHS }, author: ['CM', 'HRWHS'] },
  // §3.4 investigation (6): four under the treat verb, two WHS hazard dates
  { name: 'TreatmentActions', label: 'Treatment (plan, controls, advisor engaged)', kind: 'textarea', required: { investigation: ALWAYS }, author: ['Oversight'] },
  { name: 'ResidualLikelihood', label: 'Likelihood after treatment', kind: 'choice', required: { investigation: ALWAYS }, author: ['Oversight'] },
  { name: 'ResidualConsequence', label: 'Consequence after treatment', kind: 'choice', required: { investigation: ALWAYS }, author: ['Oversight'] },
  { name: 'ResidualRiskRating', label: 'Rating after treatment', kind: 'choice', required: { investigation: ALWAYS }, author: ['Oversight'] },
  { name: 'ResolutionAgreedOn', label: 'Hazard resolution agreed on', kind: 'day-new', required: { investigation: OPTIONAL }, author: ['HRWHS'] },
  { name: 'ResolutionCommunicatedOn', label: 'Hazard resolution communicated on', kind: 'day-new', required: { closure: R_WHS }, author: ['HRWHS'] },
  // §3.5 closure (7; ReviewDate and Status are above)
  { name: 'ClosureStatus', label: 'Closure status', kind: 'choice', author: ['system', 'Oversight'] },
  { name: 'RiskAccepted', label: 'Accepted without (further) treatment?', kind: 'choice', required: { closure: ALWAYS }, author: ['Oversight'] },
  { name: 'RiskAcceptedByUPN', label: 'Accepted by', kind: 'upn', required: { closure: R_ACCEPTED }, author: ['Oversight'] },
  { name: 'RiskAcceptedOn', label: 'Accepted on', kind: 'day-new', required: { closure: R_ACCEPTED }, author: ['Oversight'] },
  { name: 'InsuranceChecked', label: 'Insurance coverage checked?', kind: 'choice', required: { closure: ALWAYS }, author: ['Oversight'] },
  // display only
  { name: 'Created', kind: 'instant', author: ['system'], displayOnly: true },
  { name: 'RiskOwner', kind: 'person-frozen', author: [], displayOnly: true },
];

/** Risk's treat columns: `handling` columns the treat verb writes (§3.4 "[treat verb]"). */
const RISK_TREAT: ReadonlySet<string> = new Set([
  'TreatmentActions', 'ResidualLikelihood', 'ResidualConsequence', 'ResidualRiskRating',
]);

const RISK_VERBS: VerbLists = {
  lodge: ['Title', 'RiskCategory', 'BusinessUnit', 'Description'],
  triage: [
    'Likelihood', 'Consequence', 'InherentRiskRating', 'ExistingControls', 'ReviewDate', 'Status', 'OriginatingItems',
    'RiskRegisterType', 'RiskOwnerUPN', 'TreatmentUrgency', 'HazardType',
  ],
  whs: ['HazardType'],
  handling: [
    'TreatmentActions', 'ResidualLikelihood', 'ResidualConsequence', 'ResidualRiskRating', 'ResolutionAgreedOn',
    'ResolutionCommunicatedOn',
  ],
  // §1.8: the review verb resets ReviewDate and records acceptance; a retired risk is Completed + Closed (D-13-17-7).
  riskReview: ['ReviewDate', 'RiskAccepted', 'RiskAcceptedByUPN', 'RiskAcceptedOn', 'InsuranceChecked'],
  riskRetire: ['Status', 'ClosureStatus'],
  cancel: ['Status', 'ClosureStatus'],
  reopen: ['Status', 'ClosureStatus'],
};

// ---------------------------------------------------------------------------
// Continuous Improvement (FIELDSET §2.5, §3.2.5, §3.3.5, §3.4, §3.5)
// ---------------------------------------------------------------------------

const CI_COLUMNS: readonly ColumnDef[] = [
  // §3.2.5 lodge (8)
  { name: 'Title', label: 'Improvement', kind: 'text', required: { lodge: ALWAYS }, author: ['reporter', 'CI engine'] },
  { name: 'Description', label: 'Description', kind: 'textarea', required: { lodge: ALWAYS }, author: ['reporter', 'CI engine'] },
  { name: 'Issue', label: 'Issue or problem identified', kind: 'textarea', required: { lodge: ALWAYS }, author: ['reporter', 'CI engine'] },
  { name: 'Category', label: 'Category', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter', 'CI engine'] },
  { name: 'BusinessUnit', label: 'Business unit', kind: 'choice', required: { lodge: ALWAYS }, author: ['reporter', 'CI engine'] },
  { name: 'IdentifiedOn', label: 'Identified on', kind: 'day-existing', required: { lodge: ALWAYS }, author: ['reporter', 'CI engine'] },
  { name: 'Attachments', label: 'Attachments', kind: 'attachments', required: { lodge: OPTIONAL, investigation: OPTIONAL }, author: ['reporter', 'Oversight'] },
  { name: 'RaisedByUPN', label: 'Raised by', kind: 'upn', required: { lodge: ALWAYS }, author: ['reporter', 'CI engine'] },
  // §3.3.5 triage (8) + the CI engine's two (no dashboard verb)
  { name: 'OriginatingItems', label: 'Originating items', kind: 'textarea', required: { triage: OPTIONAL }, author: ['Oversight', 'CI engine'] },
  { name: 'Status', label: 'Status', kind: 'choice', author: ['system', 'Oversight'] },
  { name: 'Priority', label: 'Priority', kind: 'choice', required: { triage: ALWAYS }, author: ['Oversight'] },
  { name: 'ManagingUnit', label: 'Managing unit', kind: 'choice', required: { triage: ALWAYS }, author: ['Oversight'] },
  { name: 'PositionsResponsible', label: 'Positions responsible', kind: 'position', required: { triage: ALWAYS }, author: ['Oversight'] },
  { name: 'StartOn', label: 'Start on', kind: 'day-existing', required: { triage: ALWAYS }, author: ['Oversight'] },
  { name: 'CompleteBy', label: 'Complete by', kind: 'day-existing', required: { triage: ALWAYS }, author: ['Oversight'] },
  { name: 'OwnerUPN', label: 'Owner', kind: 'upn', required: { triage: ALWAYS }, author: ['Oversight'] },
  { name: 'ReviewDue', label: 'Review due', kind: 'day-new', author: ['CI engine'], stages: ['triage'] },
  { name: 'ReviewMethod', label: 'Review method', kind: 'textarea', author: ['CI engine'], stages: ['triage'] },
  // §3.5 closure (9; Status is above)
  { name: 'Outcome', label: 'Outcome', kind: 'textarea', required: { closure: ALWAYS }, author: ['Oversight'] },
  { name: 'ClosedOn', label: 'Closed on', kind: 'day-existing', required: { closure: ALWAYS }, author: ['Oversight'] },
  { name: 'ClosingComments', label: 'Closing comments', kind: 'textarea', required: { closure: ALWAYS }, author: ['Oversight'] },
  { name: 'ResolutionApprovedBy', kind: 'person-frozen', author: ['Oversight'], displayOnly: true, stages: ['closure'] },
  { name: 'ClosureStatus', label: 'Closure status', kind: 'choice', author: ['system', 'Oversight'] },
  { name: 'ReviewEvidence', label: 'Review evidence', kind: 'textarea', author: ['CI engine'], stages: ['closure'] },
  { name: 'ReviewVerdict', label: 'Review verdict', kind: 'choice', author: ['Oversight', 'CI engine'], stages: ['closure'] },
  { name: 'ResolutionApprovedByUPN', label: 'Resolution approved by', kind: 'upn', required: { closure: ALWAYS }, author: ['Oversight'] },
  // display only
  { name: 'Created', kind: 'instant', author: ['system'], displayOnly: true },
];

const CI_VERBS: VerbLists = {
  lodge: ['Title', 'Description', 'Issue', 'Category', 'BusinessUnit', 'IdentifiedOn', 'Attachments', 'RaisedByUPN'],
  triage: [
    'OriginatingItems', 'Status', 'Priority', 'ManagingUnit', 'PositionsResponsible', 'StartOn', 'CompleteBy',
    'OwnerUPN',
  ],
  handling: ['Attachments'],
  // §1.8: the closure verb sets ClosedOn + ClosureStatus; the approve verb sets ResolutionApprovedByUPN.
  close: ['Status', 'Outcome', 'ClosedOn', 'ClosingComments', 'ClosureStatus'],
  ciApprove: ['ResolutionApprovedByUPN'],
  cancel: ['Status', 'ClosureStatus'],
  // D-13-21-2 (ruled 13 Oct 2026, D-13-33): reopening an improvement clears its approval, so it is approved again.
  reopen: ['Status', 'ClosedOn', 'ClosureStatus', 'ResolutionApprovedByUPN'],
};

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

/** The verb order (FIELDSET stage order), and the stage each verb belongs to. */
export const VERB_ORDER: readonly RecordVerbKey[] = [
  'lodge', 'complete', 'triage', 'reportability', 'whs', 'notification', 'handling', 'readyForApproval',
  'approveAndClose', 'returnForRework', 'close', 'ciApprove', 'riskReview', 'riskRetire', 'cancel', 'closeAsDuplicate',
  'reopen',
];

/**
 * 13.2-02: verbs in the field set whose builders are not written yet, so the tests that run every verb through a
 * builder (the verb engine, the closure builders, the floor audit) skip them, and `recordClosure` does not treat
 * them as closure verbs. 13.2-03 removed `complete` (it is an engine verb now); 13.2-05 removed `closeAsDuplicate`
 * (closure), so it is empty. It is kept, empty, as the asserted gate that 13.2-10 and 13.2-12's pre-ship check read
 * (`fieldset.test.ts` and `floorAudit.test.ts` pin it to `[]`), and as the place a later verb waits for its builder.
 */
export const PENDING_13_2: ReadonlySet<RecordVerbKey> = new Set<RecordVerbKey>([]);

export const STAGE_OF_VERB: Readonly<Record<RecordVerbKey, Stage>> = {
  lodge: 'lodge',
  complete: 'lodge',
  triage: 'triage',
  reportability: 'triage',
  whs: 'triage',
  notification: 'notification',
  handling: 'investigation',
  readyForApproval: 'closure',
  approveAndClose: 'closure',
  returnForRework: 'closure',
  close: 'closure',
  ciApprove: 'closure',
  riskReview: 'closure',
  riskRetire: 'closure',
  cancel: 'closure',
  closeAsDuplicate: 'closure',
  reopen: 'closure',
};

const STAGE_ORDER: readonly Stage[] = ['lodge', 'triage', 'notification', 'investigation', 'closure'];
const TEXT_KINDS: ReadonlySet<FieldKind> = new Set<FieldKind>(['text', 'textarea', 'upn', 'position']);
/** FIELDSET §3.1: a text area is capped at 4,000 characters. */
export const TEXTAREA_CAP = 4000;

function build(
  register: RecordRegister,
  defs: readonly ColumnDef[],
  verbs: VerbLists,
  treat: ReadonlySet<string> = new Set(),
): readonly FieldSpec[] {
  const names = new Set(defs.map((d) => d.name));
  if (names.size !== defs.length) throw new Error(`fieldset: duplicate column in ${register}`);
  for (const [verb, cols] of Object.entries(verbs)) {
    for (const c of cols ?? []) {
      if (!names.has(c)) throw new Error(`fieldset: ${register} verb ${verb} names ${c}, which has no definition`);
    }
  }
  return defs.map((d): FieldSpec => {
    const own = VERB_ORDER.filter((v) => verbs[v]?.includes(d.name));
    const stageSet = new Set<Stage>([...own.map((v) => STAGE_OF_VERB[v]), ...(d.stages ?? [])]);
    const live = liveColumn(register, d.name);
    let cap: number | undefined;
    if (TEXT_KINDS.has(d.kind) && live) cap = live.multiline ? TEXTAREA_CAP : live.maxLength;
    return {
      register,
      name: d.name,
      label: d.label ?? live?.displayName ?? d.name,
      kind: d.kind,
      ...(cap !== undefined ? { cap } : {}),
      stages: STAGE_ORDER.filter((s) => stageSet.has(s)),
      verbs: own,
      ...(treat.has(d.name) ? { treat: true } : {}),
      required: d.required ?? {},
      author: d.author,
      ...(d.timeWhenKnown ? { timeWhenKnown: true } : {}),
      ...(d.displayOnly ? { displayOnly: true } : {}),
    };
  });
}

const VERBS: Readonly<Record<RecordRegister, VerbLists>> = {
  incidents: INCIDENT_VERBS,
  complaints: COMPLAINT_VERBS,
  feedback: FEEDBACK_VERBS,
  risk: RISK_VERBS,
  ci: CI_VERBS,
};

/** The field set, per register. */
export const REGISTER_FIELDS: Readonly<Record<RecordRegister, readonly FieldSpec[]>> = {
  incidents: build('incidents', INCIDENT_COLUMNS, INCIDENT_VERBS),
  complaints: build('complaints', COMPLAINT_COLUMNS, COMPLAINT_VERBS),
  feedback: build('feedback', FEEDBACK_COLUMNS, FEEDBACK_VERBS),
  risk: build('risk', RISK_COLUMNS, RISK_VERBS, RISK_TREAT),
  ci: build('ci', CI_COLUMNS, CI_VERBS),
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** One column's spec, or `undefined` when the field set does not name it on that register. */
export function fieldOf(register: RecordRegister, name: string): FieldSpec | undefined {
  return REGISTER_FIELDS[register].find((f) => f.name === name);
}

/** The columns a verb writes, in FIELDSET's order (an empty list when the register has no such verb). */
export function verbColumns(register: RecordRegister, verb: RecordVerbKey): readonly string[] {
  return VERBS[register][verb] ?? [];
}

/** The verbs a register has. */
export function verbsOf(register: RecordRegister): readonly RecordVerbKey[] {
  return VERB_ORDER.filter((v) => VERBS[register][v] !== undefined);
}

/** The office lodge's columns (FIELDSET §3.2). */
export function lodgeColumns(register: RecordRegister): readonly string[] {
  return verbColumns(register, 'lodge');
}

/** The columns a stage names (FIELDSET §3), in field-set order. */
export function stageColumns(register: RecordRegister, stage: Stage): readonly string[] {
  return REGISTER_FIELDS[register].filter((f) => f.stages.includes(stage)).map((f) => f.name);
}

/** The columns written at closure (FIELDSET §3.5; review, for Risk). */
export function closureColumns(register: RecordRegister): readonly string[] {
  return stageColumns(register, 'closure');
}

/** The live choices of a choice column, in live order (read from `liveColumns.json`, never retyped). */
export function choicesOf(register: RecordRegister, column: string): readonly string[] {
  return liveChoices(register, column);
}

/** Every register, for iteration. */
export { RECORD_REGISTERS };
